import {
    ChangeDetectorRef,
    Component,
    DestroyRef,
    HostBinding,
    inject,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    SimpleChanges,
} from '@angular/core';
import {
    NavigationDropdown,
    NavigationItem,
    NavigationLink,
} from '../../../../core/navigation/navigation-item.interface';
import { dropdownAnimation } from '@vex/animations/dropdown.animation';
import { NavigationEnd, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { filter, map } from 'rxjs/operators';
import { NavigationService } from '../../../../core/navigation/navigation.service';

import { MatIconModule } from '@angular/material/icon';
import { MatRippleModule } from '@angular/material/core';
import { NgClass, NgFor, NgIf } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CurrentUserModel } from '@shared/common/models';
import { BadgeService } from '../../../../common/services/badge.service';
import { BadgeKey } from '../../../../common/enums';
import { Store } from '@ngxs/store';
import { ChannelState } from '../../../../pages/chat/store/states/channel.state';
import { SubSink } from 'subsink';

@Component({
    selector: 'vex-sidenav-item',
    templateUrl: './sidenav-item.component.html',
    styleUrls: ['./sidenav-item.component.scss'],
    animations: [dropdownAnimation],
    standalone: true,
    imports: [NgIf, MatRippleModule, RouterLinkActive, RouterLink, MatIconModule, NgClass, NgFor],
})
export class SidenavItemComponent implements OnInit, OnChanges, OnDestroy {
    @Input({ required: true }) item!: NavigationItem;
    @Input({ required: true }) level!: number;
    public currentUser = inject(CurrentUserModel);
    private subs = new SubSink();
    isOpen = false;
    isActive = false;

    isLink = this.navigationService.isLink;
    isDropdown = this.navigationService.isDropdown;
    isSubheading = this.navigationService.isSubheading;

    private totalUnreadChat = 0;
    private readonly destroyRef: DestroyRef = inject(DestroyRef);

    constructor(
        private router: Router,
        private cd: ChangeDetectorRef,
        private navigationService: NavigationService,
        public badgeService: BadgeService,
        private store: Store,
    ) {
        // Use globalStats.activeCount — the count of open tickets (NEW, ONGOING,
        // WAITING, REOPENED) that need agent attention. This works for channels
        // where the agent isn't yet a member, unlike ChannelRead.unreadCount
        // which only exists after an agent explicitly joins a conversation.
        this.subs.sink = this.store
            .select(ChannelState.getGlobalStats)
            .pipe(map((stats) => stats?.activeCount ?? 0))
            .subscribe((count) => {
                this.totalUnreadChat = count;
            });
    }

    @HostBinding('class')
    get levelClass() {
        return `item-level-${this.level}`;
    }

    public get badgeValue() {
        if (this.item.type !== 'link' && this.item.type !== 'dropdown') return 0;
        if (!this.item.badge) return 0;
        switch (this.item.badge.key) {
            case BadgeKey.NEW_FUND_REQUEST:
                return this.badgeService.newFundRequest;

            case BadgeKey.NEW_KYC_APPLICATION:
                return this.badgeService.newKYCApplication;

            case BadgeKey.NEW_VISA_APPLICATION:
                return this.badgeService.newVisaApplication;

            case BadgeKey.NEW_PASSPORT_APPLICATION:
                return this.badgeService.newPassportApplication;

            case BadgeKey.NEW_DRIVING_PERMIT_APPLICATION:
                return this.badgeService.newDrivingPermitApplication;

            case BadgeKey.NEW_TOUR_PACKAGE_BOOKING:
                return this.badgeService.newTourPackageBooking;

            case BadgeKey.NEW_TRAVEL_INSURANCE_APPLICATION:
                return this.badgeService.newManualBookingApplication;

            case BadgeKey.NEW_NFC_CARD_REQUEST:
                return this.badgeService.newNfcCardRequest;

            case BadgeKey.UNREAD_CHAT:
                return this.totalUnreadChat;

            default:
                return 0;
        }
    }
    ngOnInit() {
        this.router.events
            .pipe(
                filter((event) => event instanceof NavigationEnd),
                filter(() => this.isDropdown(this.item)),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe(() => this.onRouteChange());

        this.navigationService.openChange$
            .pipe(
                filter(() => this.isDropdown(this.item)),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe((item) => this.onOpenChange(item));
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes && changes['item'] && this.isDropdown(this.item)) {
            this.onRouteChange();
        }
    }

    toggleOpen() {
        this.isOpen = !this.isOpen;
        this.navigationService.triggerOpenChange(this.item as NavigationDropdown);
        this.cd.markForCheck();
    }

    onOpenChange(item: NavigationDropdown) {
        if (this.isChildrenOf(this.item as NavigationDropdown, item)) {
            return;
        }

        if (this.hasActiveChilds(this.item as NavigationDropdown)) {
            return;
        }

        if (this.item !== item) {
            this.isOpen = false;
            this.cd.markForCheck();
        }
    }

    onRouteChange() {
        if (this.hasActiveChilds(this.item as NavigationDropdown)) {
            this.isActive = true;
            this.isOpen = true;
            this.navigationService.triggerOpenChange(this.item as NavigationDropdown);
            this.cd.markForCheck();
        } else {
            this.isActive = false;
            this.isOpen = false;
            this.navigationService.triggerOpenChange(this.item as NavigationDropdown);
            this.cd.markForCheck();
        }
    }

    isChildrenOf(parent: NavigationDropdown, item: NavigationDropdown): boolean {
        if (parent.children.indexOf(item) !== -1) {
            return true;
        }

        return parent.children
            .filter((child) => this.isDropdown(child))
            .some((child) => this.isChildrenOf(child as NavigationDropdown, item));
    }

    hasActiveChilds(parent: NavigationDropdown): boolean {
        return parent.children.some((child) => {
            if (this.isDropdown(child)) {
                return this.hasActiveChilds(child);
            }

            if (this.isLink(child) && !this.isFunction(child.route)) {
                return this.router.isActive(child.route as string, false);
            }

            return false;
        });
    }

    isFunction(prop: NavigationLink['route']): boolean {
        return prop instanceof Function;
    }

    ngOnDestroy(): void {
        this.subs.unsubscribe();
    }
}
